import { XMarkIcon } from "@heroicons/react/24/outline";
import DatePicker from "@via/components/date-picker/date-picker.tsx";
import Drawer from "@via/components/drawer.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import TimePicker, {
  type TimeValue,
} from "@via/components/time-picker/time-picker.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { useSaveSystemBanner } from "@via/pages/account/admin/notification-settings/notification-settings.api.tsx";
import {
  combineDateAndTime,
  extractDateFromPicker,
  formatDateForPicker,
  getUserTimezone,
  isEndAfterStart,
  MAX_MESSAGE_LENGTH,
  parseLocalDateFromYmd,
  parseTimeFromISO,
} from "@via/pages/account/admin/notification-settings/utils/date-helpers.ts";
import { classNames } from "@via/utils/helpers.tsx";
import { SystemBannerWithCreator } from "common";
import { type JSX, useState } from "react";

interface EditBannerDrawerProps {
  banner: SystemBannerWithCreator | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditBannerDrawer({
  banner,
  isOpen,
  onClose,
}: EditBannerDrawerProps): JSX.Element {
  return (
    <Drawer
      id="edit-banner"
      open={isOpen}
      setOpen={() => onClose()}
      showMinimizeButton={false}>
      {banner ? <EditBannerForm banner={banner} onClose={onClose} /> : <></>}
    </Drawer>
  );
}

function EditBannerForm({
  banner,
  onClose,
}: {
  banner: SystemBannerWithCreator;
  onClose: () => void;
}): JSX.Element {
  const { mutate: saveBanner, isPending: isSaving } = useSaveSystemBanner();
  const { setNotification, NOTIFICATION } = useNotifications();

  const [isEnabled, setIsEnabled] = useState(banner.isEnabled);
  const [primaryMessage, setPrimaryMessage] = useState(banner.primaryMessage);
  const [secondaryMessage, setSecondaryMessage] = useState(
    banner.secondaryMessage
  );
  const [startDate, setStartDate] = useState(
    formatDateForPicker(banner.startDatetime)
  );
  const [endDate, setEndDate] = useState(
    formatDateForPicker(banner.endDatetime)
  );

  const [startTime, setStartTime] = useState<TimeValue>(
    parseTimeFromISO(banner.startDatetime)
  );
  const [endTime, setEndTime] = useState<TimeValue>(
    parseTimeFromISO(banner.endDatetime)
  );

  const handleStartDateChange = (value: unknown) => {
    const nextStartDate = extractDateFromPicker(value);
    setStartDate(nextStartDate);

    if (!nextStartDate || !endDate) return;
    const startDateValue = parseLocalDateFromYmd(nextStartDate);
    const endDateValue = parseLocalDateFromYmd(endDate);
    if (startDateValue && endDateValue && endDateValue < startDateValue) {
      setEndDate("");
    }
  };

  const handleSave = () => {
    const startDatetime = combineDateAndTime(
      startDate,
      startTime.hour,
      startTime.minute,
      startTime.ampm
    );
    const endDatetime = combineDateAndTime(
      endDate,
      endTime.hour,
      endTime.minute,
      endTime.ampm
    );

    if (!startDatetime || !endDatetime) {
      setNotification({
        id: "banner-validation-error",
        message: "Invalid Date/Time",
        details: "Please select valid start and end dates with times.",
        variant: NOTIFICATION.WARNING,
      });
      return;
    }

    if (!isEndAfterStart(startDate, startTime, endDate, endTime)) {
      setNotification({
        id: "banner-range-validation-error",
        message: "Invalid Date Range",
        details: "End date/time must be after the start date/time.",
        variant: NOTIFICATION.WARNING,
      });
      return;
    }

    saveBanner(
      {
        id: banner.id,
        primaryMessage,
        secondaryMessage,
        startDatetime,
        endDatetime,
        isEnabled,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const isFormValid =
    primaryMessage &&
    secondaryMessage &&
    startDate &&
    endDate &&
    isEndAfterStart(startDate, startTime, endDate, endTime);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold leading-6 text-gray-900">
            Edit Notification Banner
          </h2>
          <button
            type="button"
            className="rounded-md text-gray-900 hover:text-gray-600 focus:outline-none"
            onClick={onClose}
            aria-label="Close edit drawer">
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Update the notification banner details.
        </p>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="space-y-4">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                Enable Banner
              </h3>
              <p className="text-xs text-gray-500">Show to all users</p>
            </div>
            <ToggleButton isOn={isEnabled} onToggle={setIsEnabled} />
          </div>

          {/* Primary Message */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Primary Message
              </label>
              <span className="text-xs text-gray-400">
                {primaryMessage.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
            <input
              type="text"
              maxLength={MAX_MESSAGE_LENGTH}
              value={primaryMessage}
              onChange={e => setPrimaryMessage(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          {/* Secondary Message */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Secondary Message
              </label>
              <span className="text-xs text-gray-400">
                {secondaryMessage.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
            <input
              type="text"
              maxLength={MAX_MESSAGE_LENGTH}
              value={secondaryMessage}
              onChange={e => setSecondaryMessage(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <DatePicker
              asSingle
              useRange={false}
              value={startDate || null}
              onChange={handleStartDateChange}
              placeholder="Select date"
            />
          </div>

          {/* Start Time */}
          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={setStartTime}
          />

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <DatePicker
              asSingle
              useRange={false}
              value={endDate || null}
              onChange={value => setEndDate(extractDateFromPicker(value))}
              minDate={parseLocalDateFromYmd(startDate) ?? undefined}
              placeholder="Select date"
            />
          </div>

          {/* End Time */}
          <TimePicker label="End Time" value={endTime} onChange={setEndTime} />

          {/* Timezone Hint */}
          <p className="text-xs text-gray-500">
            Times are interpreted in your local timezone ({getUserTimezone()}).
          </p>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-shrink-0 justify-end gap-3 border-t px-4 py-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !isFormValid}
          className={classNames(
            "inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm",
            isSaving || !isFormValid
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          )}>
          <Spinner loading={isSaving}>Save Changes</Spinner>
        </button>
      </div>
    </div>
  );
}
